import { useState, useCallback } from 'react';

export interface ToastItem {
  id: string;
  type: 'success' | 'error' | 'info' | 'warn';
  message: string;
  persistent?: boolean;
}

export function useToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const addToast = useCallback((type: ToastItem['type'], message: string, persistent = false): string => {
    const id = Math.random().toString(36).slice(2);
    setToasts(t => [...t, { id, type, message, persistent }]);
    if (!persistent) {
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
    }
    return id;
  }, []);
  const removeToast = useCallback((id: string) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);
  return { toasts, addToast, removeToast };
}

function toastColors(type: ToastItem['type']) {
  if (type === 'success') return { bg: 'var(--pos-bg, #f0fdf4)', border: 'var(--pos, #16a34a)', color: 'var(--pos, #16a34a)' };
  if (type === 'info')    return { bg: 'var(--info-bg, #eff6ff)', border: 'var(--info, #2563eb)', color: 'var(--info, #2563eb)' };
  if (type === 'warn')    return { bg: 'var(--warn-tint, #fef3cd)', border: 'var(--warn, #856404)', color: 'var(--warn, #856404)' };
  return { bg: 'var(--neg-bg, #fef2f2)', border: 'var(--neg, #dc2626)', color: 'var(--neg, #dc2626)' };
}

export function Toasts({ items, onRemove }: { items: ToastItem[]; onRemove: (id: string) => void }) {
  if (items.length === 0) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 8,
      maxWidth: 360, pointerEvents: 'none',
    }}>
      {items.map(t => {
        const { bg, border, color } = toastColors(t.type);
        return (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 12px',
            borderRadius: 6,
            fontSize: 13,
            background: bg,
            border: `1px solid ${border}`,
            color,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            pointerEvents: 'auto',
          }}>
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              onClick={() => onRemove(t.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                color: 'inherit', opacity: 0.6, fontSize: 14, lineHeight: 1, flexShrink: 0,
              }}
              aria-label="Dismiss"
            >×</button>
          </div>
        );
      })}
    </div>
  );
}
